/**
 * @name: Sticky CTA Bar
 * @description: Shows the sticky CTA bar when configured sections intersect the viewport.
 * @strict-mode: true
 */

/**
 * Friendly theme-editor names that differ from the section type / file handle.
 * Keys are lowercased trimmed names.
 * @type {Record<string, string>}
 */
const STICKY_CTA_SECTION_ALIASES = {
  'premium headwear hero': 'performance-headwear-banner',
  'premium headwear': 'performance-headwear-banner',
  'performance headwear banner': 'performance-headwear-banner',
  'product page gallery': 'product-page-gallery',
  'accordion with image': 'accordion-with-image',
  'collection favorites': 'collection-favorites',
  'image banner': 'image-banner',
  'trust bar': 'trust-bar',
  'review slider': 'review-slider',
};

/**
 * @param {string} value
 * @returns {string}
 */
const handleize = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');

/**
 * @extends {HTMLElement}
 */
class StickyCtaBar extends HTMLElement {
  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {IntersectionObserver|null} */
  #headerObserver = null;

  /** @type {Set<Element>} */
  #visibleTriggers = new Set();

  /** @type {boolean} */
  #headerPassed = false;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#headerObserver?.disconnect();
    this.#observer = null;
    this.#headerObserver = null;
    this.#visibleTriggers.clear();
  }

  #init() {
    const isDesignMode = this.dataset.designMode === 'true';

    if (isDesignMode) {
      this.#setVisible(true);
      return;
    }

    const config = this.#readConfig();
    const triggers = this.#resolveTriggers(config.triggers || []);

    if (triggers.length > 0) {
      this.#observeTriggers(triggers);
      return;
    }

    this.#observeHeaderFallback();
  }

  /**
   * @returns {{triggers: string[]}}
   */
  #readConfig() {
    const configEl = this.querySelector('[data-sticky-cta-bar-config]');

    if (!configEl) {
      return { triggers: [] };
    }

    try {
      const parsed = JSON.parse(configEl.textContent || '{}');

      return {
        triggers: Array.isArray(parsed.triggers)
          ? parsed.triggers.filter((item) => typeof item === 'string' && item.trim() !== '')
          : [],
      };
    } catch (error) {
      console.error('Sticky CTA bar: failed to parse config', error);
      return { triggers: [] };
    }
  }

  /**
   * @param {string[]} values
   * @returns {Element[]}
   */
  #resolveTriggers(values) {
    /** @type {Element[]} */
    const elements = [];

    for (const raw of values) {
      const value = raw.trim();

      if (!value) continue;

      const matched = this.#findSectionsByNameOrClass(value);

      for (const element of matched) {
        if (!elements.includes(element)) {
          elements.push(element);
        }
      }
    }

    return elements;
  }

  /**
   * Accepts a theme-editor section name or a CSS class.
   * @param {string} input
   * @returns {Element[]}
   */
  #findSectionsByNameOrClass(input) {
    try {
      const className = input.startsWith('.') ? input.slice(1).trim() : input.trim();
      /** @type {Element[]} */
      const matches = [];

      if (className && !className.includes(' ')) {
        const byClass = document.querySelectorAll(`.${CSS.escape(className)}`);

        for (const candidate of byClass) {
          const sectionEl = candidate.closest('.shopify-section') || candidate;

          if (!matches.includes(sectionEl)) {
            matches.push(sectionEl);
          }
        }

        if (matches.length > 0) {
          return matches;
        }
      }

      const handles = this.#resolveHandlesFromName(input);

      for (const handle of handles) {
        const candidates = [
          ...document.querySelectorAll(`[data-section-type="${CSS.escape(handle)}"]`),
          ...document.querySelectorAll(`.${CSS.escape(handle)}`),
          ...document.querySelectorAll(`.${CSS.escape(handle)}-section`),
        ];

        for (const candidate of candidates) {
          const sectionEl = candidate.closest('.shopify-section') || candidate;

          if (!matches.includes(sectionEl)) {
            matches.push(sectionEl);
          }
        }
      }

      return matches;
    } catch (error) {
      console.warn('Sticky CTA bar: could not resolve section', input, error);
      return [];
    }
  }

  /**
   * @param {string} input
   * @returns {string[]}
   */
  #resolveHandlesFromName(input) {
    const normalized = input.toLowerCase().trim();
    const handles = [];

    if (STICKY_CTA_SECTION_ALIASES[normalized]) {
      handles.push(STICKY_CTA_SECTION_ALIASES[normalized]);
    }

    const handle = handleize(input);

    if (handle && !handles.includes(handle)) {
      handles.push(handle);
    }

    return handles;
  }

  /**
   * @param {Element[]} triggers
   */
  #observeTriggers(triggers) {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#visibleTriggers.add(entry.target);
          } else {
            this.#visibleTriggers.delete(entry.target);
          }
        }

        this.#setVisible(this.#visibleTriggers.size > 0);
      },
      {
        root: null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.05,
      }
    );

    for (const trigger of triggers) {
      this.#observer.observe(trigger);
    }
  }

  #observeHeaderFallback() {
    const header =
      document.querySelector('#shopify-section-header') ||
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('header') ||
      document.querySelector('[data-section-type="header"]');

    if (!header) {
      this.#setVisible(true);
      return;
    }

    this.#headerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#headerPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        }

        this.#setVisible(this.#headerPassed);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    this.#headerObserver.observe(header);

    const rect = header.getBoundingClientRect();
    this.#headerPassed = rect.bottom < 0;
    this.#setVisible(this.#headerPassed);
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.hidden = !visible;
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
}

customElements.define('sticky-cta-bar', StickyCtaBar);
